import type { jsPDF } from "jspdf";
import type { GroupAddressReport, GroupAddressUsage } from "./groupAddressReport";

export type PdfTextColumn = { x: number; width: number };
export type GroupAddressLayout = { main: PdfTextColumn[]; usage: PdfTextColumn[] };
const gap = 12;

/** Uses exactly the same displayed values for measurement and rendering. */
export function groupAddressUsageValues(detail: GroupAddressUsage): string[] {
  return [
    detail.ancestorTag.replace(/^\s*-\s*/, ""),
    detail.ancestorName,
    detail.channelDescription,
    detail.signalNumber,
  ];
}

/** Measures the longest explicit line with the currently selected font, including a rounding allowance. */
function measure(doc: jsPDF, text: string): number {
  let width = 0;
  for (const line of text.split(/\r?\n/)) width = Math.max(width, doc.getTextWidth(line));
  return Math.ceil(width) + 1;
}

/** Keeps short columns compact and limits long columns equally when the page cannot fit every unwrapped value. */
function fitColumns(preferred: number[], left: number, right: number): PdfTextColumn[] {
  const available = right - left - gap * (preferred.length - 1);
  let lower = 0;
  let upper = Math.max(...preferred);
  for (let iteration = 0; iteration < 40; iteration += 1) {
    const cap = (lower + upper) / 2;
    let total = 0;
    for (const width of preferred) total += Math.min(width, cap);
    if (total > available) upper = cap;
    else lower = cap;
  }
  let x = left;
  return preferred.map((preferredWidth) => {
    const width = Math.min(preferredWidth, lower);
    const column = { x, width };
    x += width + gap;
    return column;
  });
}

/** Measures all document rows after font registration, producing stable column positions for every page. */
export function measureGroupAddressLayout(doc: jsPDF, report: GroupAddressReport): GroupAddressLayout {
  const left = 51;
  const right = doc.internal.pageSize.getWidth() - 51;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const mainWidths = [measure(doc, "Adresse"), measure(doc, "Name")];
  if (!report.detailedUsage) mainWidths.push(measure(doc, "Verwendungen"));
  const usageWidths = [1, 1, 1, 1];
  for (const section of report.sections) {
    for (const row of section.rows) {
      doc.setFont("helvetica", row.level === 2 ? "bold" : "normal");
      doc.setFontSize(9);
      mainWidths[0] = Math.max(mainWidths[0], measure(doc, row.address));
      mainWidths[1] = Math.max(mainWidths[1], measure(doc, row.name || "–"));
      if (!report.detailedUsage) mainWidths[2] = Math.max(mainWidths[2], measure(doc, String(row.usages)));
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      for (const detail of row.usageDetails ?? []) {
        const values = groupAddressUsageValues(detail);
        for (let index = 0; index < values.length; index += 1)
          usageWidths[index] = Math.max(usageWidths[index], measure(doc, values[index]));
      }
    }
  }
  const main = fitColumns(mainWidths, left, right);
  const usage = fitColumns(usageWidths, main[1].x + 10, right);
  return { main, usage };
}
